import React, { useEffect, useRef, useState } from 'react';

import {
    GetInactiveTerritoriesInput,
    GetInactiveTerritoriesOutput,
    Territory
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import DatePicker from '@amzn/meridian/date-picker';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';

import { contractConfigurationClient } from 'src/api/client';
import { ALL_SERVICE } from 'src/constants/context';
import { IS_REQUIRED } from 'src/constants/errorMessages';
import { AppContext } from 'src/contexts/appContext';
import { ILaunchDateProps, ILaunchDateValues } from 'src/types/ILaunchDates';
import { dateComparator } from 'src/util/grid/sort';
import { validateRequired } from 'src/util/validationsUtil';

import './InputLaunchDate.scss';

export const InputLaunchDate = ({
    onNext,
    onCancel,
    launchDateValues,
    handleLaunchDateValuesChange,
    setLaunchDateValues
}: ILaunchDateProps) => {
    const { metadata } = React.useContext(AppContext);
    const services = Object.values(metadata.serviceMapping).filter(
        (service) => service.serviceName !== ALL_SERVICE.NAME
    );
    const [territories, setTerritories] = useState<Array<Territory>>([]);
    const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});
    const [isFormValid, setIsFormValid] = useState(false);
    const firstUpdate = useRef(true);
    const { setTopAlertMsg } = React.useContext(AppContext);

    useEffect(() => {
        setIsFormValid(checkFormValidity());
    }, [launchDateValues, errorMessages]);

    useEffect(() => {
        contractConfigurationClient
            .getInactiveTerritories(
                GetInactiveTerritoriesInput.clone({
                    serviceId: parseInt(launchDateValues.serviceID)
                })
            )
            .subscribe(
                (response: GetInactiveTerritoriesOutput) => {
                    setTerritories(response.territories ? response.territories : []);
                },
                (error: UnknownCoralError) => {
                    if (!firstUpdate) {
                        console.error('getInactiveTerritories unexpected error', error.receivedException.message);
                        setTopAlertMsg('Error retrieving territories with GetInactiveTerritories API');
                    } else {
                        firstUpdate.current = false;
                    }
                }
            );
    }, [launchDateValues.serviceID]);

    const checkFormValidity = () => {
        const requiredFields = ['serviceID', 'territoryID', 'launchDate'];

        for (let field of requiredFields) {
            if (!launchDateValues[field as keyof ILaunchDateValues]) {
                return false;
            }
        }
        return Object.keys(errorMessages).length === 0;
    };

    const onBlurField = (field: keyof ILaunchDateValues) => {
        let errorMessage = '';
        let value = launchDateValues[field];
        if (field === 'serviceID' || field === 'territoryID') {
            if (!validateRequired(value?.toString())) {
                errorMessage = IS_REQUIRED;
            }
        } else if (field === 'launchDate') {
            if (!validateRequired(value)) {
                errorMessage = IS_REQUIRED;
            } else if (launchDateValues.endDate && dateComparator(value, launchDateValues.endDate) >= 0) {
                errorMessage = 'Launch date must be before end date';
            }
        } else if (field === 'endDate') {
            if (launchDateValues.launchDate && value && dateComparator(launchDateValues.launchDate, value) >= 0) {
                errorMessage = 'End date must be after start date';
            }
        }

        setErrorMessages((prevState) => {
            const newState = { ...prevState };
            if (errorMessage === '') {
                delete newState[field];
            } else {
                newState[field] = errorMessage;
            }
            return newState;
        });
    };

    return (
        <>
            <Heading level={1}>Set Launch Date</Heading>
            <Column spacingInset="500 none none none" spacing="400">
                <Select
                    label="Service Name *"
                    value={launchDateValues.serviceID}
                    onChange={(newVal) => {
                        setLaunchDateValues((prevState) => ({
                            ...prevState,
                            serviceID: newVal,
                            territoryID: '',
                            territoryCode: ''
                        }));
                    }}
                    onBlur={() => onBlurField('serviceID')}
                    error={!!errorMessages.serviceID}
                    errorMessage={errorMessages.serviceID}
                    size="xlarge"
                >
                    {services.map((service) => (
                        <SelectOption
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            value={service.serviceId!}
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            label={service.serviceName!}
                            key={service.serviceId}
                        />
                    ))}
                </Select>
                <Select
                    label="Territory *"
                    value={launchDateValues.territoryCode}
                    valueLabel={() => launchDateValues.territoryCode}
                    onChange={(newVal) => {
                        setLaunchDateValues((prevState) => ({
                            ...prevState,
                            territoryID: newVal.territoryId,
                            territoryCode: newVal.territoryCode
                        }));
                    }}
                    onBlur={() => onBlurField('territoryID')}
                    error={!!errorMessages.territoryID}
                    errorMessage={errorMessages.territoryID}
                    size="xlarge"
                >
                    {territories.map((territory) => (
                        <SelectOption
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            value={Object.assign(territory!)}
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            label={territory.territoryCode!}
                            key={territory.territoryId}
                        />
                    ))}
                </Select>
                <Row alignmentHorizontal="justify">
                    <DatePicker
                        width="265px"
                        locale="en-CA"
                        value={launchDateValues.launchDate}
                        onChange={(newVal) => handleLaunchDateValuesChange('launchDate', newVal)}
                        onBlur={() => onBlurField('launchDate')}
                        error={!!errorMessages.launchDate}
                        errorMessage={errorMessages.launchDate}
                        label="UTC Launch Date *"
                        size="xlarge"
                    />
                    <DatePicker
                        width="265px"
                        locale="en-CA"
                        value={launchDateValues.endDate}
                        onChange={(newVal) => handleLaunchDateValuesChange('endDate', newVal)}
                        onBlur={() => onBlurField('endDate')}
                        error={!!errorMessages.endDate}
                        errorMessage={errorMessages.endDate}
                        label="UTC End Date"
                        size="xlarge"
                    />
                </Row>
                <Column>
                    <Row alignmentHorizontal="end">
                        <Button type="link" onClick={onCancel} aria-label="Cancel launch">
                            Cancel
                        </Button>
                        <Button type="primary" disabled={!isFormValid} onClick={onNext} aria-label="Go to next step.">
                            Next
                        </Button>
                    </Row>
                </Column>
            </Column>
        </>
    );
};
